import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import {
  Compass, Flag, LineChart, Calendar, TrendingUp, Loader2,
} from 'lucide-react';
import { horizonApi, type HorizonResponse } from '../api/services';
import { colourForDriverId } from '../theme/tokens';
import HelpPanel from '../components/HelpPanel';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Horizon — quarterly rhythm view. Four sections answer "how is this quarter
// tracking?" in one screen: drivers with progress, KPIs against target,
// upcoming outcomes in the next 90 days, and a four-week completion trend.
// All data flows from a single `GET /api/horizon` round trip so the page
// doesn't need to orchestrate five fetches.
// ---------------------------------------------------------------------------

function formatDelta(k: HorizonResponse['kpis'][number]): string | null {
  if (k.currentValue === undefined || k.targetValue === undefined) return null;
  const unit = k.unit ?? '';
  return `${k.currentValue}${unit} of ${k.targetValue}${unit}`;
}

export default function HorizonPage() {
  const [data, setData] = useState<HorizonResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await horizonApi.get();
        if (!cancelled) setData(response);
      } catch (err: any) {
        if (!cancelled) setError(err.response?.data?.error || err.message || 'Could not load Horizon.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[240px] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="error">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!data) return null;

  const onTrackCount = data.kpis.filter((k) => k.onTrack).length;

  return (
    <div className="mx-auto max-w-[1280px] p-4 md:p-6">
      <div className="mb-6 flex items-center gap-2">
        <Compass className="h-7 w-7 text-primary" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Horizon</h1>
          <p className="text-sm text-muted-foreground">
            {data.label} · Q{data.quarter} · {format(parseISO(data.quarterStart), 'd MMM')} to {format(parseISO(data.quarterEnd), 'd MMM yyyy')}
          </p>
        </div>
      </div>

      <HelpPanel title="About Horizon" storageKey="horizon">
        Horizon is the quarterly rhythm view. It answers "is this quarter still on track?" in one screen:
        driver progress, KPIs against target, milestones landing in the next ninety days, and how the last
        four weeks of planned work actually finished.
      </HelpPanel>

      {/* Quarter health summary */}
      <Card className="mb-4 border-l-[3px] border-l-primary">
        <CardContent className="py-3">
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm font-semibold">This quarter at a glance</p>
            <span className="inline-flex items-center rounded-full border border-border bg-transparent px-2 py-0.5 text-[11px] font-medium">
              {data.drivers.length} active drivers
            </span>
            <span
              className={cn(
                'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold',
                onTrackCount === data.kpis.length
                  ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
                  : 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
              )}
            >
              {onTrackCount} of {data.kpis.length} KPIs on track
            </span>
            <span className="inline-flex items-center rounded-full border border-border bg-transparent px-2 py-0.5 text-[11px] font-medium">
              {data.upcomingOutcomes.length} milestones in the next 90 days
            </span>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Drivers */}
        <Card className="h-full">
          <CardContent>
            <div className="mb-3 flex items-center gap-2">
              <Flag className="h-5 w-5 text-primary" />
              <p className="text-base font-semibold">Strategic drivers</p>
            </div>
            {data.drivers.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No active drivers yet. Add an annual driver under Work › Drivers to anchor this quarter's execution.
              </p>
            ) : (
              <div className="flex flex-col gap-3">
                {data.drivers.map((d) => {
                  const colour = colourForDriverId(d.id);
                  return (
                    <div key={d.id}>
                      <div className="mb-1 flex items-center gap-2">
                        <span aria-hidden className="h-2 w-2 rounded-full" style={{ backgroundColor: colour }} />
                        <p className="flex-1 text-sm font-medium">{d.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {Math.round(d.progressPercentage)}% · {d.kpisOnTrack}/{d.kpiCount} KPIs on track
                        </p>
                      </div>
                      <div className="h-1.5 w-full overflow-hidden rounded-full" style={{ backgroundColor: `${colour}26` }}>
                        <div
                          className="h-full transition-all"
                          style={{
                            width: `${Math.min(100, Math.max(0, d.progressPercentage))}%`,
                            backgroundColor: colour,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* KPIs */}
        <Card className="h-full">
          <CardContent>
            <div className="mb-3 flex items-center gap-2">
              <LineChart className="h-5 w-5 text-primary" />
              <p className="text-base font-semibold">KPIs against target</p>
            </div>
            {data.kpis.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No KPIs defined. Structured KPIs are added on each driver.
              </p>
            ) : (
              <div className="flex flex-col gap-3">
                {data.kpis.map((k) => (
                  <div key={k.id} className="flex items-center gap-2">
                    <span
                      aria-hidden
                      className={cn(
                        'h-2 w-2 shrink-0 rounded-full',
                        k.onTrack ? 'bg-emerald-500' : 'bg-amber-500',
                      )}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">{k.name}</p>
                      {k.driverTitle && <p className="text-xs text-muted-foreground">{k.driverTitle}</p>}
                    </div>
                    <p
                      className={cn(
                        'whitespace-nowrap text-xs font-semibold',
                        k.onTrack
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : 'text-amber-700 dark:text-amber-400',
                      )}
                    >
                      {formatDelta(k) ?? 'Unreported'}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming outcomes */}
        <Card className="h-full">
          <CardContent>
            <div className="mb-3 flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              <p className="text-base font-semibold">Next 90 days</p>
            </div>
            {data.upcomingOutcomes.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No project outcomes dated in the next ninety days. Add target dates on project outcomes to populate this panel.
              </p>
            ) : (
              <div className="flex flex-col gap-1.5">
                {data.upcomingOutcomes.map((o) => (
                  <div key={o.id} className="rounded-md bg-muted/30 p-2">
                    <p className="text-sm font-medium">{o.title}</p>
                    <div className="mt-0.5 flex items-center gap-2">
                      <p className="text-xs text-muted-foreground">{o.projectTitle}</p>
                      {o.targetDate && (
                        <span className="inline-flex items-center rounded-full border border-border bg-transparent px-2 py-0 text-[11px] font-medium">
                          {format(parseISO(o.targetDate), 'd MMM')}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Weekly completion trend */}
        <Card className="h-full">
          <CardContent>
            <div className="mb-3 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              <p className="text-base font-semibold">Four-week completion trend</p>
            </div>
            {data.weeklyCompletion.every((w) => w.planned === 0) ? (
              <p className="text-sm text-muted-foreground">
                No planned work in the last four weeks to trend on.
              </p>
            ) : (
              <div className="flex flex-col gap-3">
                {data.weeklyCompletion.map((w) => {
                  const pct = w.planned === 0 ? 0 : Math.round((w.completed / w.planned) * 100);
                  const tone = pct >= 80
                    ? 'bg-emerald-500'
                    : pct >= 50
                      ? 'bg-amber-500'
                      : 'bg-rose-500';
                  const toneText = pct >= 80
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : pct >= 50
                      ? 'text-amber-700 dark:text-amber-400'
                      : 'text-rose-600 dark:text-rose-400';
                  return (
                    <div key={w.weekStart}>
                      <div className="mb-1 flex items-center gap-2">
                        <p className="min-w-[120px] text-xs text-muted-foreground">
                          {format(parseISO(w.weekStart), 'd MMM')} to {format(parseISO(w.weekEnd), 'd MMM')}
                        </p>
                        <p className="flex-1 text-sm">{w.completed}/{w.planned} items</p>
                        <p className={cn('text-xs font-semibold', toneText)}>{pct}%</p>
                      </div>
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                        <div className={cn('h-full transition-all', tone)} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
